import { WorkflowState } from '@amzn/awscat-pattern-library-service-model';
import React, { type JSX } from 'react';
import { withRouter } from 'react-router';
import constants from '../../common/constants';
import RouterPaths from '../../common/RouterPaths';
import { withAppContext } from '../../contexts/AppContext';
import BarRaiserContentViewerControls from './action-controls/BarRaiserContentViewerControls';
import ContentViewer from './ContentViewer';
import { ShowAllCommentsButton } from '../inline-comment';
import { Box, Container, Header } from '@amzn/awsui-components-core';
import {
  isInBarRaiserPublishedReview,
  isWorkflowReviewedByCurrentUser,
} from '../../api/ContentUtil';

/**
 * BAR RAISER'S VIEW of a content draft.
 *
 * Bar raisers are quality gatekeepers who evaluate published content.
 * This view extends ContentViewer (Template Method pattern) and overrides hooks to:
 *   - Allow editing (so bar raisers can add inline comments)
 *   - Always show review feedback (bar raisers need to see all review history)
 *   - Render a "Show All Comments" action button
 *   - Render BarRaiserContentViewerControls with an "Evaluate Publication Quality" button
 *   - Disable controls when: content is locked, not yet reviewed, already evaluated by this user
 *   - Customize breadcrumbs to point back to the Bar Raiser Dashboard
 *
 * NOTE: Unlike AuthorContentViewer, this does NOT override canDisplayView() —
 * any authenticated user can access this page (access control is handled at the route level).
 * It also does NOT set draftViewerRole, so it defaults to null (not Author/Reviewer/etc.),
 * which means the lock-info banner WILL show if another user is editing.
 */
class BarRaiserContentViewer extends ContentViewer {
  // ─── TEMPLATE METHOD OVERRIDES (configuration flags) ────────────────────────

  // Allow editing so bar raisers can add inline comments to the content
  protected isEditingAllowed = true;
  // Show publication version history for context during quality evaluation
  protected showPublicationHistory = true;
  // Enable per-section inline commenting UI
  protected showSectionalComments = true;
  // Show the last quality evaluation score so bar raisers can see previous assessments
  protected showLastBarRaiserEvaluation: boolean = true;

  // ─── REVIEW FEEDBACK VISIBILITY ─────────────────────────────────────────────

  // Always show review feedback — bar raisers need full visibility into the review
  // history to make informed quality evaluations.
  protected showReviewFeedback = () => {
    return true;
  };

  // ─── ACTIONS (status/action section above controls) ─────────────────────────

  // Renders a "Show All Comments" button that toggles inline comment visibility
  // across all sections. This helps bar raisers quickly see all feedback at once.
  // @ts-expect-error TS2416 - strict migration
  protected renderActions = () => {
    return (
      <Container header={<Header variant="h3">Actions</Header>}>
        <Box padding={{ top: 's' }}>
          <ShowAllCommentsButton handleShowAllComments={this.handleShowAllComments} />
        </Box>
      </Container>
    );
  };

  // ─── ACTION CONTROLS (bottom of page) ──────────────────────────────────────

  // Renders BarRaiserContentViewerControls — an "Evaluate Publication Quality" button
  // that opens the quality questionnaire in a new tab.
  //
  // The button is DISABLED when any of these conditions are true:
  //   - workflow is locked (another user is editing)
  //   - draft is NOT in REVIEWED state AND NOT in a published-review state
  //     (bar raisers can only evaluate content that has been reviewed or is published)
  //   - the current user already evaluated this content (prevent duplicate evaluations)
  //
  // isInBarRaiserPublishedReview: true when workflow is UNMODIFIED (published) and
  //   has no quality score or was recommended for unpublishing — allows re-evaluation.
  // isWorkflowReviewedByCurrentUser: true when this user was the bar raiser who
  //   already evaluated the latest publication workflow.
  protected renderControls(): false | JSX.Element {
    const { workflow } = this.state;
    const { appContext } = this.props;
    const userId = appContext?.authnInfo?.userProfile?.userId;
    // @ts-expect-error TS18048 - strict migration
    const isLocked = !!workflow.lock;
    // @ts-expect-error TS18048 - strict migration
    const isReviewed = workflow.draftState === WorkflowState.REVIEWED;
    const isInPublishedReview = isInBarRaiserPublishedReview(workflow);
    // @ts-expect-error TS2345 - strict migration
    const isReviewedByCurrentUser = isWorkflowReviewedByCurrentUser(userId, workflow);
    const disabled =
      !!workflow && (isLocked || (!isReviewed && !isInPublishedReview) || isReviewedByCurrentUser);

    return (
      <BarRaiserContentViewerControls
        contentId={workflow?.contentId}
        workflow={workflow}
        disabled={disabled}
      />
    );
  }

  // ─── BREADCRUMBS ────────────────────────────────────────────────────────────

  // Override breadcrumbs to point back to the Bar Raiser Dashboard instead of the
  // default home page. Uses location.state.navigateBackTo if available (for when
  // the user arrived from a specific filtered view), otherwise falls back to home.
  protected getBreadcrumbItems(): Array<{ text: string; href: string }> {
    return [
      {
        href: this.props.location.state?.navigateBackTo || RouterPaths.HOME_PAGE,
        text: constants.BAR_RAISER_DASHBOARD_HEADER,
      },
    ];
  }

  // ─── PRIVATE HELPERS ────────────────────────────────────────────────────────

  // Toggles inline comments visible on the reviewer page.
  // Uses the inherited setShowInlineCommentsInReviewerPage() from ContentViewer
  // which updates state and causes DraftContentData to show all inline comments.
  private handleShowAllComments = () => {
    this.setShowInlineCommentsInReviewerPage(true);
  };
}

export default withRouter(withAppContext(BarRaiserContentViewer));
